import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Lightbulb, BookOpen, FileText, Landmark, DollarSign, Building2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export type ArchiveItem =
  | {
      kind: "idea";
      id: string;
      title: string;
      summary: string | null;
      cover: string | null;
      category: string | null;
      type: "concept" | "project";
      date: string;
      vote_count: number;
      href: string;
    }
  | {
      kind: "curated";
      id: string;
      title: string;
      summary: string | null;
      cover: string | null;
      category: string | null;
      source_org: string;
      source_url: string;
      date: string;
      href: string;
    }
  | {
      kind: "case_study";
      id: string;
      title: string;
      summary: string | null;
      cover: string | null;
      category: string | null;
      outcome: "succeeded" | "partial" | "paused" | "failed" | "ongoing";
      lessons_count: number;
      date: string;
      href: string;
    }
  | {
      kind: "article";
      id: string;
      title: string;
      summary: string | null;
      cover: string | null;
      category: string | null;
      author: string | null;
      date: string;
      href: string;
    }
  | {
      kind: "legislation";
      id: string;
      title: string;
      summary: string | null;
      cover: null;
      category: string | null;
      bill_id: string | null;
      status: string | null;
      county: string | null;
      date: string;
      href: string;
      external_url: string | null;
    }
  | {
      kind: "grant";
      id: string;
      title: string;
      summary: string | null;
      cover: null;
      category: string | null;
      next_deadline: string | null;
      date: string;
      href: string;
      external_url: string | null;
    }
  | {
      kind: "partner";
      id: string;
      title: string;
      summary: string | null;
      cover: string | null;
      category: string | null;
      counties: string[] | null;
      tier: string | null;
      date: string;
      href: string;
    };

const OUTCOME_CLS: Record<string, string> = {
  succeeded: "bg-primary/15 text-primary",
  partial: "bg-amber-500/15 text-amber-500",
  paused: "bg-muted text-muted-foreground",
  failed: "bg-destructive/15 text-destructive",
  ongoing: "bg-secondary text-secondary-foreground",
};

const KIND_META: Record<ArchiveItem["kind"], { label: string; Icon: typeof Lightbulb; cls: string }> = {
  idea: { label: "Community", Icon: Lightbulb, cls: "bg-secondary text-secondary-foreground" },
  curated: { label: "EM Curated", Icon: BookOpen, cls: "bg-accent/15 text-accent-foreground border border-accent/30" },
  case_study: { label: "Case Study", Icon: BookOpen, cls: "bg-primary/15 text-primary" },
  article: { label: "Insight", Icon: FileText, cls: "bg-secondary text-secondary-foreground" },
  legislation: { label: "Legislation", Icon: Landmark, cls: "bg-secondary text-secondary-foreground" },
  grant: { label: "Grant", Icon: DollarSign, cls: "bg-secondary text-secondary-foreground" },
  partner: { label: "Partner", Icon: Building2, cls: "bg-secondary text-secondary-foreground" },
};

const ArchiveCard = ({ item }: { item: ArchiveItem }) => {
  const meta = KIND_META[item.kind];
  const Icon = meta.Icon;
  return (
    <article className="break-inside-avoid mb-4 group bg-card border border-border rounded-xl overflow-hidden hover:border-primary/40 transition-colors">
      {item.cover && (
        <Link to={item.href} className="block overflow-hidden">
          <img
            src={item.cover}
            alt={item.title}
            loading="lazy"
            className="w-full max-h-72 object-cover group-hover:scale-[1.02] transition-transform duration-300"
          />
        </Link>
      )}
      <div className="p-4">
        <div className="flex flex-wrap gap-1.5 mb-2">
          <Badge className={`text-xs inline-flex items-center gap-1 ${meta.cls}`}>
            <Icon className="h-3 w-3" />
            {meta.label}
          </Badge>
          {item.kind === "idea" && (
            <Badge variant="outline" className="text-xs capitalize">{item.type}</Badge>
          )}
          {item.kind === "case_study" && (
            <Badge className={`text-xs capitalize ${OUTCOME_CLS[item.outcome]}`}>
              {item.outcome}
            </Badge>
          )}
          {item.kind === "legislation" && item.bill_id && (
            <Badge variant="outline" className="text-xs">{item.bill_id}</Badge>
          )}
          {item.kind === "legislation" && item.status && (
            <Badge variant="outline" className="text-xs capitalize">{item.status}</Badge>
          )}
          {item.kind === "partner" && item.tier && (
            <Badge variant="outline" className="text-xs capitalize">{item.tier}</Badge>
          )}
          {item.category && (
            <Badge variant="outline" className="text-xs">{item.category}</Badge>
          )}
        </div>

        <Link
          to={item.href}
          className="block font-display font-semibold text-foreground leading-snug mb-1.5 hover:text-primary transition-colors"
        >
          {item.title}
        </Link>

        {item.summary && (
          <p className="text-sm text-muted-foreground line-clamp-3">{item.summary}</p>
        )}

        <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
          {item.kind === "curated" ? (
            <a
              href={item.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 truncate hover:text-primary"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3 w-3 shrink-0" />
              <span className="truncate">Originally from {item.source_org}</span>
            </a>
          ) : item.kind === "idea" ? (
            <span>▲ {item.vote_count}</span>
          ) : item.kind === "case_study" ? (
            <span>{item.lessons_count} lesson{item.lessons_count === 1 ? "" : "s"}</span>
          ) : item.kind === "article" ? (
            <span className="truncate">{item.author ? `by ${item.author}` : "Insight"}</span>
          ) : item.kind === "legislation" ? (
            <span className="truncate">{item.county ?? "Alabama"}</span>
          ) : item.kind === "grant" ? (
            <span className="truncate">
              {item.next_deadline
                ? `Due ${new Date(item.next_deadline).toLocaleDateString()}`
                : "Rolling"}
            </span>
          ) : item.kind === "partner" ? (
            <span className="truncate">
              {item.counties && item.counties.length > 0 ? item.counties.slice(0, 2).join(", ") : "Alabama"}
            </span>
          ) : (
            <span />
          )}
          <span className="shrink-0">
            {formatDistanceToNow(new Date(item.date), { addSuffix: true })}
          </span>
        </div>
      </div>
    </article>
  );
};

export default ArchiveCard;